import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  alpha,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { getContrastRatio } from "@mui/material/styles";
import { useTenantTranslation } from "hooks/formatters";
import {
  useMaterialColor,
  useMaterialDisplayName,
} from "src/hooks/useMaterials";
import { equalSets } from "../heats/utils";

const getContrastText = (hexColor: string): string =>
  getContrastRatio(hexColor, "#fff") > 4.5 ? "#fff" : "#111";

interface MaterialsToggleProps {
  ignorableMaterialSets: string[][];
  ignoredMaterials: Set<string>;
  setIgnoredMaterials: (ignoredMaterials: Set<string>) => void;
  materialOrdering?: string[];
}

export const MaterialsToggle = ({
  ignorableMaterialSets,
  ignoredMaterials,
  setIgnoredMaterials,
  materialOrdering,
}: MaterialsToggleProps) => {
  const { t } = useTenantTranslation();
  const { getColor } = useMaterialColor();
  const materialDisplayName = useMaterialDisplayName();
  const ignorableMaterialSetsAsSet = ignorableMaterialSets.map(
    (materials) => new Set(materials.filter((m) => m !== ""))
  );

  const ignorableMaterials = [
    ...new Set(ignorableMaterialSetsAsSet.flatMap((set) => [...set])),
  ]
    .filter((material) => material !== "")
    .sort((a, b) => {
      if (!materialOrdering) return a.localeCompare(b);
      return (
        (!materialOrdering.includes(a)
          ? Infinity
          : materialOrdering.indexOf(a)) -
        (!materialOrdering.includes(b) ? Infinity : materialOrdering.indexOf(b))
      );
    });

  const furtherIgnorableMaterials = ignorableMaterials.filter((material) =>
    ignorableMaterialSetsAsSet.some((set) =>
      equalSets(set, new Set([material, ...ignoredMaterials]))
    )
  );

  const unignorableMaterials = ignorableMaterials.filter((material) =>
    ignorableMaterialSetsAsSet.some((set) =>
      equalSets(
        set,
        new Set([...ignoredMaterials].filter((m) => m !== material))
      )
    )
  );

  const disableMaterial = (material: string) => {
    const cannotIgnore =
      ![...ignoredMaterials].includes(material) &&
      !furtherIgnorableMaterials.includes(material);
    const cannotUnignore =
      [...ignoredMaterials].includes(material) &&
      !unignorableMaterials.includes(material);
    return cannotIgnore || cannotUnignore;
  };

  const selectedMaterials = ignorableMaterials.filter(
    (material) => !ignoredMaterials.has(material)
  );

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newSelected: string[]
  ) => {
    const newIgnored = new Set(
      ignorableMaterials.filter((m) => !newSelected.includes(m))
    );
    setIgnoredMaterials(newIgnored);
  };

  if (ignorableMaterials.length === 0) {
    return null;
  }

  return (
    <Stack direction="column" alignItems="flex-start" spacing={0.5}>
      <Typography variant="body2" color="text.secondary">
        {t("includeMaterials")}
      </Typography>
      <ToggleButtonGroup
        value={selectedMaterials}
        onChange={handleChange}
        size="small"
      >
        {ignorableMaterials.map((material) => {
          const materialColor = getColor(material);
          const textColor = getContrastText(materialColor);
          const isSelected = selectedMaterials.includes(material);
          return (
            <ToggleButton
              key={material}
              value={material}
              disabled={disableMaterial(material)}
              sx={{
                textTransform: "none",
                fontWeight: "fontWeightSemiBold",
                px: 2,
                gap: 0.5,
                "&:only-child": {
                  borderRadius: 1,
                },
                "&.Mui-selected": {
                  backgroundColor: materialColor,
                  color: textColor,
                  "&:hover": {
                    backgroundColor: alpha(materialColor, 0.8),
                  },
                },
              }}
            >
              {isSelected ? (
                <CheckIcon sx={{ fontSize: 16 }} />
              ) : (
                <CloseIcon sx={{ fontSize: 16 }} />
              )}
              {materialDisplayName(material)}
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>
    </Stack>
  );
};
